/* eslint-disable @typescript-eslint/no-explicit-any */
// components/RecipeCard.tsx
'use client';
import {
  ChefHat,
  ChevronDown,
  ChevronUp,
  Clock,
  Info,
  Printer,
  Star,
  Tag,
  Users,
  Utensils,
} from 'lucide-react';
import { useState } from 'react';

const formatTime = (time: string | undefined) => {
  if (!time) return null;
  // Convert ISO 8601 duration to readable format
  // Example: PT35M -> 35 minutes
  const match = time.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return time;

  const hours = match[1] ? parseInt(match[1]) : 0;
  const minutes = match[2] ? parseInt(match[2]) : 0;

  if (hours && minutes) {
    return `${hours} hr ${minutes} min`;
  } else if (hours) {
    return `${hours} hr`;
  } else if (minutes) {
    return `${minutes} min`;
  }

  return time;
};

type RecipeDetails = {
  name?: string;
  description?: string;
  prepTime?: string;
  cookTime?: string;
  totalTime?: string;
  recipeYield?: string;
  recipeCategory?: string;
  recipeCuisine?: string;
  keywords?: Array<string>;
  recipeIngredient?: Array<string>;
  recipeInstructions?: Array<{
    children?: Array<{
      marks?: Array<string>;
      text?: string;
      _type: 'span';
      _key: string;
    }>;
    style?: 'normal' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'blockquote';
    listItem?: 'bullet' | 'number';
    markDefs?: Array<{
      href?: string;
      _type: 'link';
      _key: string;
    }>;
    level?: number;
    _type: 'block';
    _key: string;
  }>;
  nutrition?: {
    calories?: string;
    fatContent?: string;
    carbohydrateContent?: string;
  };
  aggregateRating?: {
    ratingValue?: number;
    ratingCount?: number;
  };
  video?: string;
};

export default function RecipeCard({ recipeDetails }: { recipeDetails: RecipeDetails }) {
  const [showIngredients, setShowIngredients] = useState(true);
  const [showInstructions, setShowInstructions] = useState(true);
  const [isCollapsedHeader] = useState(false);

  if (!recipeDetails) return null;

  const prepTime = formatTime(recipeDetails.prepTime);
  const cookTime = formatTime(recipeDetails.cookTime);
  const totalTime = formatTime(recipeDetails.totalTime);

  // Extract instruction text from the complex structure
  const getInstructionText = (instruction: any) => {
    if (!instruction?.children) return '';
    return instruction.children
      .map((child: any) => child.text || '')
      .filter(Boolean)
      .join(' ');
  };

  const printRecipe = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const instructions =
      recipeDetails.recipeInstructions
        ?.map(instruction => {
          return `<li>${getInstructionText(instruction)}</li>`;
        })
        .join('') || '';

    const content = `
      <html>
        <head>
          <title>${recipeDetails.name || 'Recipe'}</title>
          <style>
            @media print {
              @page { margin: 2cm; }
            }
            body { 
              font-family: 'Helvetica', 'Arial', sans-serif; 
              line-height: 1.6; 
              max-width: 800px; 
              margin: 0 auto; 
              padding: 20px;
              color: #333;
            }
            h1 { 
              color: #d97706; 
              font-size: 28px;
              margin-bottom: 10px;
            }
            h2 {
              color: #78350f;
              font-size: 22px;
              margin-top: 30px;
              border-bottom: 1px solid #e5e7eb;
              padding-bottom: 8px;
            }
            .section { margin-bottom: 30px; }
            .ingredients li, .instructions li { margin-bottom: 12px; }
            .meta { 
              display: flex; 
              gap: 25px; 
              margin: 20px 0; 
              flex-wrap: wrap;
              background-color: #f9fafb;
              padding: 15px;
              border-radius: 8px;
            }
            .meta-item { 
              display: flex; 
              align-items: center;
              font-weight: 500;
            }
            .meta-label {
              font-weight: normal;
              color: #6b7280;
              margin-right: 6px;
            }
            .description {
              font-style: italic;
              color: #4b5563;
              margin-bottom: 20px;
            }
            .nutrition {
              background-color: #f9fafb;
              padding: 15px;
              border-radius: 8px;
              margin-top: 30px;
            }
            .nutrition-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 15px;
            }
            .nutrition-item {
              text-align: center;
              padding: 10px;
              background-color: white;
              border-radius: 6px;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            }
            .nutrition-label {
              font-size: 14px;
              color: #6b7280;
              margin-bottom: 5px;
            }
            .nutrition-value {
              font-weight: bold;
              font-size: 18px;
            }
            .tags {
              margin-top: 25px;
            }
            .tag {
              display: inline-block;
              background-color: #e5e7eb;
              border-radius: 20px;
              padding: 5px 12px;
              margin-right: 8px;
              margin-bottom: 8px;
              font-size: 14px;
            }
          </style>
        </head>
        <body>
          <h1>${recipeDetails.name || 'Recipe'}</h1>
          <p class="description">${recipeDetails.description || ''}</p>
          
          <div class="meta">
            ${prepTime ? `<div class="meta-item"><span class="meta-label">Prep:</span> ${prepTime}</div>` : ''}
            ${cookTime ? `<div class="meta-item"><span class="meta-label">Cook:</span> ${cookTime}</div>` : ''}
            ${totalTime ? `<div class="meta-item"><span class="meta-label">Total:</span> ${totalTime}</div>` : ''}
            ${recipeDetails.recipeYield ? `<div class="meta-item"><span class="meta-label">Serves:</span> ${recipeDetails.recipeYield}</div>` : ''}
            ${recipeDetails.recipeCuisine ? `<div class="meta-item"><span class="meta-label">Cuisine:</span> ${recipeDetails.recipeCuisine}</div>` : ''}
            ${recipeDetails.recipeCategory ? `<div class="meta-item"><span class="meta-label">Category:</span> ${recipeDetails.recipeCategory}</div>` : ''}
          </div>
          
          <div class="section">
            <h2>Ingredients</h2>
            <ul class="ingredients">
              ${recipeDetails.recipeIngredient?.map(ingredient => `<li>${ingredient}</li>`).join('') || ''}
            </ul>
          </div>
          
          <div class="section">
            <h2>Instructions</h2>
            <ol class="instructions">
              ${instructions}
            </ol>
          </div>
          
          ${
            recipeDetails.nutrition
              ? `
          <div class="nutrition">
            <h2>Nutrition (per serving)</h2>
            <div class="nutrition-grid">
              ${
                recipeDetails.nutrition.calories
                  ? `
              <div class="nutrition-item">
                <div class="nutrition-label">Calories</div>
                <div class="nutrition-value">${recipeDetails.nutrition.calories}</div>
              </div>`
                  : ''
              }
              ${
                recipeDetails.nutrition.fatContent
                  ? `
              <div class="nutrition-item">
                <div class="nutrition-label">Fat</div>
                <div class="nutrition-value">${recipeDetails.nutrition.fatContent}</div>
              </div>`
                  : ''
              }
              ${
                recipeDetails.nutrition.carbohydrateContent
                  ? `
              <div class="nutrition-item">
                <div class="nutrition-label">Carbs</div>
                <div class="nutrition-value">${recipeDetails.nutrition.carbohydrateContent}</div>
              </div>`
                  : ''
              }
            </div>
          </div>
          `
              : ''
          }

          ${
            recipeDetails.keywords && recipeDetails.keywords.length > 0
              ? `
          <div class="tags">
            <h2>Tags</h2>
            <div>
              ${
                Array.isArray(recipeDetails.keywords)
                  ? recipeDetails.keywords
                      .map(keyword => `<span class="tag">${keyword}</span>`)
                      .join('')
                  : ''
              }
            </div>
          </div>
          `
              : ''
          }
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(content);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  return (
    <div className="my-8 overflow-hidden rounded-2xl border border-amber-100 bg-white shadow-xl transition-all duration-300 md:my-12 dark:border-amber-900/30 dark:bg-gray-900">
      <div
        className={`relative overflow-hidden transition-all duration-300 ${
          isCollapsedHeader ? 'py-6' : 'py-8 md:py-10'
        } bg-gradient-to-br from-amber-400 to-orange-600 px-6 text-white md:px-8`}
      >
        <div className="relative z-10">
          <h2 className="mb-3 text-2xl leading-tight font-bold md:text-3xl lg:text-4xl">
            {recipeDetails.name}
          </h2>
          <p className="max-w-3xl text-base text-amber-50 md:text-lg">
            {recipeDetails.description}
          </p>

          {/* Rating Display */}
          {recipeDetails.aggregateRating && (
            <div className="mt-4 block w-full items-center justify-between md:flex">
              <div className="flex items-center">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      fill={
                        i < Math.round(recipeDetails.aggregateRating?.ratingValue || 0)
                          ? 'currentColor'
                          : 'none'
                      }
                      className={`h-5 w-5 ${
                        i < Math.round(recipeDetails.aggregateRating?.ratingValue || 0)
                          ? 'text-yellow-300'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="ml-2 font-medium text-amber-50">
                  {recipeDetails.aggregateRating.ratingValue}/5 (
                  {recipeDetails.aggregateRating.ratingCount} reviews)
                </span>
              </div>

              <button
                onClick={printRecipe}
                className="mt-4 flex items-center rounded-full bg-purple-500 px-4 py-2 text-sm font-medium backdrop-blur-sm transition-all duration-200 hover:bg-white/30 md:mt-0"
              >
                <Printer className="mr-2 h-4 w-4" />
                Print Recipe
              </button>
            </div>
          )}
        </div>

        {/* Print Button */}

        {/* Decorative elements */}
        <div className="absolute -right-6 -bottom-6 h-24 w-24 rounded-full bg-amber-300/20 backdrop-blur-sm"></div>
        <div className="absolute -top-8 -left-8 h-32 w-32 rounded-full bg-orange-500/20 backdrop-blur-sm"></div>
      </div>

      {/* Recipe Meta Info */}
      <div className="grid grid-cols-2 gap-4 border-b border-amber-100/70 bg-amber-50/50 p-6 md:grid-cols-4 dark:border-amber-900/30 dark:bg-amber-900/10">
        {prepTime && (
          <div className="flex flex-col items-center text-center">
            <Clock className="mb-2 h-6 w-6 text-amber-600 dark:text-amber-400" />
            <p className="text-sm font-medium text-amber-900/60 dark:text-amber-400/70">
              Prep Time
            </p>
            <p className="font-bold text-amber-900 dark:text-amber-200">{prepTime}</p>
          </div>
        )}

        {cookTime && (
          <div className="flex flex-col items-center text-center">
            <ChefHat className="mb-2 h-6 w-6 text-amber-600 dark:text-amber-400" />
            <p className="text-sm font-medium text-amber-900/60 dark:text-amber-400/70">
              Cook Time
            </p>
            <p className="font-bold text-amber-900 dark:text-amber-200">{cookTime}</p>
          </div>
        )}

        {totalTime && (
          <div className="flex flex-col items-center text-center">
            <Clock className="mb-2 h-6 w-6 text-amber-600 dark:text-amber-400" />
            <p className="text-sm font-medium text-amber-900/60 dark:text-amber-400/70">
              Total Time
            </p>
            <p className="font-bold text-amber-900 dark:text-amber-200">{totalTime}</p>
          </div>
        )}

        {recipeDetails.recipeYield && (
          <div className="flex flex-col items-center text-center">
            <Users className="mb-2 h-6 w-6 text-amber-600 dark:text-amber-400" />
            <p className="text-sm font-medium text-amber-900/60 dark:text-amber-400/70">Servings</p>
            <p className="font-bold text-amber-900 dark:text-amber-200">
              {recipeDetails.recipeYield}
            </p>
          </div>
        )}
      </div>

      {/* Recipe Content */}
      <div className="p-2 md:p-8">
        {/* Cuisine & Category */}
        {(recipeDetails.recipeCuisine || recipeDetails.recipeCategory) && (
          <div className="my-4 flex flex-wrap gap-3 md:mt-0">
            {recipeDetails.recipeCuisine && (
              <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                <Tag className="mr-1.5 h-4 w-4" />
                {recipeDetails.recipeCuisine} Cuisine
              </span>
            )}

            {recipeDetails.recipeCategory && (
              <span className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1.5 text-sm font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                <Utensils className="mr-1.5 h-4 w-4" />
                {recipeDetails.recipeCategory}
              </span>
            )}
          </div>
        )}

        {/* Ingredients Section */}
        <div className="mb-8 rounded-xl border border-amber-100 bg-white shadow-sm transition-all hover:shadow-md dark:border-amber-900/30 dark:bg-gray-800/50">
          <button
            className="flex w-full items-center justify-between rounded-t-xl bg-amber-50 p-4 text-left font-medium text-amber-900 transition hover:bg-amber-100/70 dark:bg-amber-900/20 dark:text-amber-100 dark:hover:bg-amber-900/30"
            onClick={() => setShowIngredients(!showIngredients)}
          >
            <h3 className="flex items-center text-xl font-bold">
              <Info className="mr-2 h-5 w-5 text-amber-600 dark:text-amber-400" />
              Ingredients
            </h3>
            {showIngredients ? (
              <ChevronUp className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            )}
          </button>

          {showIngredients && (
            <div className="p-5 md:p-6">
              <ul className="grid gap-3 md:grid-cols-2">
                {recipeDetails.recipeIngredient?.map((ingredient, index) => (
                  <li key={index} className="flex items-start">
                    <span className="mt-1.5 mr-3 h-2 w-2 flex-shrink-0 rounded-full bg-amber-500"></span>
                    <span className="text-gray-800 dark:text-gray-200">{ingredient}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Instructions Section */}
        <div className="mb-8 rounded-xl border border-amber-100 bg-white shadow-sm transition-all hover:shadow-md dark:border-amber-900/30 dark:bg-gray-800/50">
          <button
            className="flex w-full items-center justify-between rounded-t-xl bg-amber-50 p-4 text-left font-medium text-amber-900 transition hover:bg-amber-100/70 dark:bg-amber-900/20 dark:text-amber-100 dark:hover:bg-amber-900/30"
            onClick={() => setShowInstructions(!showInstructions)}
          >
            <h3 className="flex items-center text-xl font-bold">
              <ChefHat className="mr-2 h-5 w-5 text-amber-600 dark:text-amber-400" />
              Instructions
            </h3>
            {showInstructions ? (
              <ChevronUp className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            )}
          </button>

          {showInstructions && (
            <div className="p-5 md:p-6">
              <ol className="space-y-6">
                {recipeDetails.recipeInstructions?.map((instruction, index) => (
                  <li key={index} className="group flex">
                    <div className="relative">
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-500 text-sm font-bold text-white">
                        {index + 1}
                      </span>
                      {index < recipeDetails.recipeInstructions?.length - 1 && (
                        <div className="absolute top-8 left-1/2 h-full w-0.5 -translate-x-1/2 bg-amber-200 dark:bg-amber-700"></div>
                      )}
                    </div>
                    <div className="ml-4 flex-1">
                      <p className="text-gray-800 dark:text-gray-200">
                        {getInstructionText(instruction)}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>

        {/* Nutrition Section */}
        {recipeDetails.nutrition && (
          <div className="mb-8 rounded-xl border border-amber-100 bg-amber-50/50 p-6 shadow-sm dark:border-amber-900/30 dark:bg-amber-900/10">
            <h3 className="mb-5 flex items-center text-xl font-bold text-amber-900 dark:text-amber-100">
              <Info className="mr-2 h-5 w-5 text-amber-600 dark:text-amber-400" />
              Nutrition (per serving)
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {recipeDetails.nutrition.calories && (
                <div className="group overflow-hidden rounded-lg bg-white p-4 shadow-sm transition-all hover:shadow-md dark:bg-gray-800">
                  <p className="text-sm font-medium text-amber-800/70 dark:text-amber-300/70">
                    Calories
                  </p>
                  <p className="mt-1 text-lg font-bold text-amber-900 dark:text-amber-200">
                    {recipeDetails.nutrition.calories}
                  </p>
                  <div className="mt-2 h-1 w-full bg-amber-100 dark:bg-amber-900/50">
                    <div className="h-full w-3/4 bg-amber-500 transition-all group-hover:w-full"></div>
                  </div>
                </div>
              )}

              {recipeDetails.nutrition.fatContent && (
                <div className="group overflow-hidden rounded-lg bg-white p-4 shadow-sm transition-all hover:shadow-md dark:bg-gray-800">
                  <p className="text-sm font-medium text-amber-800/70 dark:text-amber-300/70">
                    Fat
                  </p>
                  <p className="mt-1 text-lg font-bold text-amber-900 dark:text-amber-200">
                    {recipeDetails.nutrition.fatContent}
                  </p>
                  <div className="mt-2 h-1 w-full bg-amber-100 dark:bg-amber-900/50">
                    <div className="h-full w-1/2 bg-amber-500 transition-all group-hover:w-2/3"></div>
                  </div>
                </div>
              )}

              {recipeDetails.nutrition.carbohydrateContent && (
                <div className="group overflow-hidden rounded-lg bg-white p-4 shadow-sm transition-all hover:shadow-md dark:bg-gray-800">
                  <p className="text-sm font-medium text-amber-800/70 dark:text-amber-300/70">
                    Carbs
                  </p>
                  <p className="mt-1 text-lg font-bold text-amber-900 dark:text-amber-200">
                    {recipeDetails.nutrition.carbohydrateContent}
                  </p>
                  <div className="mt-2 h-1 w-full bg-amber-100 dark:bg-amber-900/50">
                    <div className="h-full w-2/3 bg-amber-500 transition-all group-hover:w-full"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Keywords/Tags */}
        {recipeDetails.keywords && recipeDetails.keywords.length > 0 && (
          <div className="mt-8">
            <h3 className="mb-4 flex items-center text-lg font-medium text-amber-900 dark:text-amber-100">
              <Tag className="mr-2 h-5 w-5 text-amber-600 dark:text-amber-400" />
              Tags
            </h3>
            <div className="flex flex-wrap gap-2">
              {Array.isArray(recipeDetails.keywords) ? (
                recipeDetails.keywords.map((keyword, index) => (
                  <span
                    key={index}
                    className="rounded-full bg-amber-100 px-3 py-1.5 text-sm text-amber-800 transition-all hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:hover:bg-amber-900/60"
                  >
                    {keyword}
                  </span>
                ))
              ) : (
                <span className="rounded-full bg-amber-100 px-3 py-1.5 text-sm text-amber-800 transition-all hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:hover:bg-amber-900/60">
                  {recipeDetails.keywords}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
